import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { F0Card, F0Heading, F0Text } from '@factorialco/f0-react'
import { Stack, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Sparkles } from '@factorialco/f0-react/icons/app'
import { TicketingTopCategoriesForCreateDialogDocument } from 'generated/resources/graphql'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import type { CreateTicketTabHandle, CreateTicketTabSubmitState } from '.'

interface CreateWithAiTabProps {
  active: boolean
  isOpen: boolean
  onLaunchAiTicketCreation: (
    message: string,
    options?: { source?: 'description' | 'category' }
  ) => void
  onSubmitStateChange?: (state: CreateTicketTabSubmitState) => void
}

export const CreateWithAiTab = forwardRef<CreateTicketTabHandle, CreateWithAiTabProps>(
  function CreateWithAiTab({ active, isOpen, onLaunchAiTicketCreation, onSubmitStateChange }, ref) {
    const [hasPrompt, setHasPrompt] = useState(false)
    const [prompt, setPrompt] = useState('')
    const { data, loading } = useQuery(TicketingTopCategoriesForCreateDialogDocument, {
      variables: { limit: 3 },
      skip: !isOpen || !active,
    })

    const topCategories = useMemo(
      () => data?.ticketing?.topCategoriesConnection.nodes ?? [],
      [data]
    )
    const showTopCategoriesSkeleton = loading && topCategories.length === 0

    const submitPrompt = useCallback(
      (message: string, source: 'description' | 'category') => {
        const trimmedMessage = message.trim()
        if (!trimmedMessage) return

        onLaunchAiTicketCreation(trimmedMessage, { source })
      },
      [onLaunchAiTicketCreation]
    )

    const handlePromptChange = useCallback((value: string) => {
      setPrompt(value)
      setHasPrompt(value.trim().length > 0)
    }, [])

    const handlePresetClick = useCallback(
      (categoryName: string) => {
        const message = i18n.t('ticketing.inbox.create.ai_preset_prompt', { categoryName })
        submitPrompt(message, 'category')
      },
      [submitPrompt]
    )

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          submitPrompt(prompt, 'description')
          setPrompt('')
          setHasPrompt(false)
        },
      }),
      [prompt, submitPrompt]
    )

    useEffect(() => {
      onSubmitStateChange?.({ disabled: !prompt.trim() })
    }, [onSubmitStateChange, prompt])

    useEffect(() => {
      if (!isOpen) {
        setPrompt('')
        setHasPrompt(false)
      }
    }, [isOpen])

    return (
      <Stack
        paddingX="p-8"
        paddingY="p-8"
        gap="4"
        data-testid="ticket-ai-create-form"
        style={{ maxWidth: 640, margin: '0 auto' }}
      >
        <F0Heading content={i18n.t('ticketing.inbox.create.ai_prompt_label')} />

        <Textarea
          label={i18n.t('ticketing.inbox.create.ai_prompt_label')}
          value={prompt}
          onChange={handlePromptChange}
          placeholder={i18n.t('ticketing.inbox.create.ai_prompt_placeholder')}
          rows={4}
          hideLabel
        />

        {(showTopCategoriesSkeleton || topCategories.length > 0) && (
          <Stack gap="2">
            <F0Text
              variant="description"
              content={i18n.t('ticketing.inbox.create.ai_presets_label')}
            />
            <Stack gap="2">
              {showTopCategoriesSkeleton
                ? Array.from({ length: 3 }).map((_, index) => (
                    <Stack
                      key={`top-category-skeleton-${index}`}
                      aria-hidden
                      className="animate-pulse rounded-lg border border-solid border-f1-border-secondary bg-f1-background p-3"
                      gap="2"
                    >
                      <div className="h-4 w-1/3 rounded bg-f1-background-secondary" />
                    </Stack>
                  ))
                : topCategories.map((category) => {
                    const handleClick = hasPrompt
                      ? undefined
                      : () => handlePresetClick(category.name)

                    return (
                      <Stack
                        key={category.id}
                        aria-disabled={hasPrompt}
                        style={
                          hasPrompt
                            ? {
                                color: 'var(--f1-foreground-secondary)',
                                cursor: 'not-allowed',
                                opacity: 0.55,
                              }
                            : undefined
                        }
                      >
                        <F0Card
                          avatar={{ type: 'icon', icon: Sparkles }}
                          title={category.name}
                          description={category.description || ''}
                          onClick={handleClick}
                          compact
                        />
                      </Stack>
                    )
                  })}
            </Stack>
          </Stack>
        )}
      </Stack>
    )
  }
)
